import React from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, FolderKanban, Users } from 'lucide-react';

import { TenantQuotaUsageSummary, useQuotaUsage } from '@/api/settings/useQuotaUsage';

import { CustomCard } from '../DashboardPage/Components/MetricComponents';

function formatBillingPeriodLabel(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key.trim());
  if (!m) return key;
  const y = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  return new Date(y, monthIndex, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatUsageLine(summary: TenantQuotaUsageSummary, kind: 'events' | 'users' | 'workspaces'): string {
  const used =
    kind === 'events'
      ? summary.eventsUsedThisBillingPeriod
      : kind === 'users'
        ? summary.usersTracked
        : summary.workspacesUsed;
  const max =
    kind === 'events'
      ? summary.maxEventsPerBillingPeriod
      : kind === 'users'
        ? summary.maxUsers
        : summary.maxWorkspaces;

  if (max === null) return `${used}`;
  if (max === -1) return `${used}`;
  return `${used} / ${max}`;
}

function sublineForMetric(
  t: (k: string, o?: Record<string, unknown>) => string,
  summary: TenantQuotaUsageSummary,
  kind: 'events' | 'users' | 'workspaces',
): string {
  const max =
    kind === 'events'
      ? summary.maxEventsPerBillingPeriod
      : kind === 'users'
        ? summary.maxUsers
        : summary.maxWorkspaces;
  const remaining =
    kind === 'events'
      ? summary.eventsRemaining
      : kind === 'users'
        ? summary.usersRemaining
        : summary.workspacesRemaining;

  if (max === null) return t('SettingsPage.usageLimitUnknown');
  if (max === -1) return t('SettingsPage.usageUnlimited');
  return t('SettingsPage.usageRemaining', { count: remaining ?? 0 });
}

const SettingsUsagePanel = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuotaUsage();

  return (
    <CustomCard className="mb-8" title={t('SettingsPage.usagePanelTitle')}>
      {isLoading ? (
        <p className="text-sm text-custom-darkSilver">{t('SettingsPage.usageLoading')}</p>
      ) : isError ? (
        <p className="text-sm text-custom-red">
          {t('SettingsPage.usageError')}
          {error instanceof Error ? `: ${error.message}` : ''}
        </p>
      ) : data ? (
        <div className="space-y-4">
          <div className="text-sm text-custom-darkSilver">
            <span className="font-medium text-custom-black">{t('SettingsPage.usageBillingPeriodLabel')}:</span>{' '}
            {formatBillingPeriodLabel(data.billingPeriodKey)}
            {data.packageName ? (
              <>
                {' '}
                · {t('SettingsPage.usagePlanLabel')}: {data.packageName}
              </>
            ) : (
              <> · {t('SettingsPage.usageNoPlan')}</>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-custom-lightSilver bg-custom-aliceBlue/30 px-4 py-3">
              <div className="flex items-center gap-2 text-custom-black font-medium text-sm mb-2">
                <Users className="w-4 h-4 text-custom-blue" aria-hidden />
                {t('SettingsPage.usageTrackedUsersTitle')}
              </div>
              <p className="text-2xl font-semibold text-custom-black tabular-nums">{formatUsageLine(data, 'users')}</p>
              <p className="text-xs text-custom-darkSilver mt-1">{sublineForMetric(t, data, 'users')}</p>
            </div>

            <div className="rounded-lg border border-custom-lightSilver bg-custom-aliceBlue/30 px-4 py-3">
              <div className="flex items-center gap-2 text-custom-black font-medium text-sm mb-2">
                <Activity className="w-4 h-4 text-custom-blue" aria-hidden />
                {t('SettingsPage.usageEventsThisPeriodTitle')}
              </div>
              <p className="text-2xl font-semibold text-custom-black tabular-nums">{formatUsageLine(data, 'events')}</p>
              <p className="text-xs text-custom-darkSilver mt-1">{sublineForMetric(t, data, 'events')}</p>
            </div>

            <div className="rounded-lg border border-custom-lightSilver bg-custom-aliceBlue/30 px-4 py-3">
              <div className="flex items-center gap-2 text-custom-black font-medium text-sm mb-2">
                <FolderKanban className="w-4 h-4 text-custom-blue" aria-hidden />
                {t('SettingsPage.usageWorkspacesTitle', { defaultValue: 'Workspaces' })}
              </div>
              <p className="text-2xl font-semibold text-custom-black tabular-nums">
                {formatUsageLine(data, 'workspaces')}
              </p>
              <p className="text-xs text-custom-darkSilver mt-1">{sublineForMetric(t, data, 'workspaces')}</p>
            </div>
          </div>
        </div>
      ) : null}
    </CustomCard>
  );
};

export default SettingsUsagePanel;
